import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import FavoriteButton from "@/components/FavoriteButton";
import { useUserAuth } from "@/context/UserAuthContext";
import { PROGRAMS, PROGRAM_COLORS, PROGRAM_LABELS } from "@/data/programs";
import { Heart, Share2, Check } from "lucide-react";

export default function MyPrograms() {
  const { user, loading, favorites, loginWithGoogle } = useUserAuth();
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const saved = PROGRAMS.filter((p) => favorites.includes(p.slug));

  async function shareList() {
    setSharing(true);
    try {
      const { data } = await api.post("/me/favorites/share");
      const url = `${window.location.origin}/shared/${data.share_id}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Share link copied — send it to a friend!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create a share link right now.");
    } finally {
      setSharing(false);
    }
  }

  return (
    <section className="pt-24 pb-16 min-h-[60vh]">
      <SEO title="My Programs" description="Your saved HOFA programs across the ERSEH framework." path="/my-programs" />
      <div className="max-w-[1140px] mx-auto px-6">
        <Eyebrow>My Programs</Eyebrow>
        <h1 className="text-[clamp(34px,4.5vw,52px)] mb-4 max-w-[24ch] font-display font-bold">
          The threads you chose to follow.
        </h1>

        {loading && <p className="text-ink/60">Loading…</p>}

        {!loading && !user && (
          <div className="border border-dashed border-ink/20 rounded-[16px] p-10 text-center max-w-[560px]" data-testid="my-programs-signin-prompt">
            <Heart className="mx-auto text-berry mb-4" size={28} />
            <p className="text-ink/75 mb-6">
              Sign in with Google to save the programs you care about and find them here anytime.
            </p>
            <button
              onClick={loginWithGoogle}
              data-testid="my-programs-signin-btn"
              className="bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition"
            >
              Sign in with Google
            </button>
          </div>
        )}

        {!loading && user && saved.length === 0 && (
          <div className="border border-dashed border-ink/20 rounded-[16px] p-10 text-center max-w-[560px]" data-testid="my-programs-empty">
            <p className="text-ink/75 mb-6">
              Nothing saved yet. Tap the heart on any program to keep it here.
            </p>
            <Link to="/programs" data-testid="my-programs-browse-link" className="inline-block bg-palm text-white font-bold rounded-full px-6 py-3 hover:shadow-xl transition no-underline">
              Browse programs
            </Link>
          </div>
        )}

        {!loading && user && saved.length > 0 && (
          <>
            <div className="mb-8">
              <button
                type="button"
                onClick={shareList}
                disabled={sharing}
                data-testid="my-programs-share-btn"
                className="inline-flex items-center gap-2 border-2 border-palm text-palm font-bold rounded-full px-6 py-3 hover:bg-palm hover:text-white transition disabled:opacity-60"
              >
                {copied ? <><Check size={17} /> Link copied!</> : <><Share2 size={17} /> {sharing ? "Creating link…" : "Share my list"}</>}
              </button>
              <p className="text-sm text-ink/55 mt-2">Friends see your current picks — update them anytime.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-programs-grid">
            {saved.map((p) => (
              <div key={p.slug} className="relative">
                <Link
                  to={`/programs/${p.slug}`}
                  data-testid={`my-program-card-${p.slug}`}
                  className="bg-white border border-ink/10 rounded-[14px] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition no-underline text-ink flex flex-col h-full"
                >
                  <div className="aspect-[16/9] overflow-hidden bg-ink/5">
                    <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 flex flex-col gap-2.5 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white"
                        style={{ background: PROGRAM_COLORS[p.key] }}
                      >
                        {PROGRAM_LABELS[p.key]}
                      </span>
                      {p.isNew && (
                        <span className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full bg-mango text-palm-deep">New</span>
                      )}
                    </div>
                    <h3 className="text-lg font-display">{p.title}</h3>
                    {p.full && <p className="text-sm font-semibold text-ink/60">{p.full}</p>}
                    <p className="text-[15px] text-ink/75 flex-1">{p.tagline}</p>
                    <span className="text-palm font-bold text-sm mt-2">Read more →</span>
                  </div>
                </Link>
                <FavoriteButton slug={p.slug} className="absolute top-3 right-3 z-10" />
              </div>
            ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
